import { useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent } from "react";

// Replacement for react-photo-gallery (unmaintained, no React 19 peer range),
// covering the one layout cem uses: direction="column" (masonry). Same markup
// (div.react-photo-gallery--gallery > div > absolutely positioned <img>),
// same column breakpoints, placement (shortest column first), rounding and
// click payload, so the video gallery lays out exactly as before.
export interface GalleryPhoto {
  key?: string;
  src: string;
  width: number;
  height: number;
  description?: string | null;
  [extra: string]: unknown;
}

export interface GalleryClick<P extends GalleryPhoto> {
  index: number;
  photo: P;
  previous: P | null;
  next: P | null;
}

interface ColumnGalleryProps<P extends GalleryPhoto> {
  photos: P[];
  onClick?: (event: MouseEvent<HTMLImageElement>, click: GalleryClick<P>) => void;
  margin?: number;
  columns?: number | ((containerWidth: number) => number);
}

const round = (value: number, decimals = 0) => Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);

const defaultColumns = (width: number) => (width >= 1500 ? 4 : width >= 900 ? 3 : width >= 500 ? 2 : 1);

export function computeColumnLayout<P extends GalleryPhoto>(photos: P[], columns: number, containerWidth: number, margin: number) {
  const colWidth = (containerWidth - margin * 2 * columns) / columns;
  const lefts = Array.from({ length: columns }, (_, i) => round(i * (colWidth + margin * 2), 1));
  const tops = Array(columns).fill(0) as number[];
  let containerHeight = 0;
  const thumbs = photos.map((photo) => {
    const width = round(colWidth, 1);
    const height = round((photo.height / photo.width) * colWidth, 1);
    const col = tops.reduce((best, top, i) => (top < tops[best] ? i : best), 0);
    const placed = { photo, width, height, top: tops[col], left: lefts[col] };
    tops[col] += height + margin * 2;
    containerHeight = Math.max(...tops);
    return placed;
  });
  return { thumbs, containerHeight };
}

export default function ColumnGallery<P extends GalleryPhoto>({ photos, onClick, margin = 2, columns }: ColumnGalleryProps<P>) {
  const [containerWidth, setContainerWidth] = useState(0);
  const galleryEl = useRef<HTMLDivElement>(null);
  // The placeholder and the gallery are different elements: re-observe when it swaps
  const showingGallery = containerWidth > 0 && photos.length > 0;

  useLayoutEffect(() => {
    const el = galleryEl.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === "undefined") {
      setContainerWidth(Math.floor(el.getBoundingClientRect().width));
      return undefined;
    }
    let frame: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const width = Math.floor(entries[0].contentRect.width);
      frame = requestAnimationFrame(() => setContainerWidth(width));
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [showingGallery]);

  // No width until the first measurement: render a placeholder to measure
  if (!showingGallery) return <div ref={galleryEl}>{" "}</div>;

  const cols = typeof columns === "function" ? columns(containerWidth) : columns ?? defaultColumns(containerWidth);
  // Subtract 1px because the browser may round up
  const { thumbs, containerHeight } = computeColumnLayout(photos, cols, containerWidth - 1, margin);

  return (
    <div className="react-photo-gallery--gallery">
      <div ref={galleryEl} style={{ position: "relative", height: containerHeight }}>
        {thumbs.map(({ photo, width, height, top, left }, index) => {
          const style: CSSProperties = { margin, display: "block", position: "absolute", left, top };
          if (onClick) style.cursor = "pointer";
          return (
            <img
              key={photo.key || photo.src}
              src={photo.src}
              width={width}
              height={height}
              alt={photo.description || ""}
              style={style}
              onClick={
                onClick
                  ? (event) => onClick(event, { index, photo, previous: photos[index - 1] || null, next: photos[index + 1] || null })
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}
